import { useEffect, useState, useCallback } from "react";
import { UserPlus, UserMinus, Plus, Clock, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const SEP_TYPES = { RESIGNATION: "Pengunduran Diri", CONTRACT_COMPLETED: "Kontrak Selesai", TERMINATION: "Pemutusan Kerja", OTHER: "Lainnya" };

function LifecycleList({ type }) {
  const { user } = useAuth();
  const canFinal = ["HR_SUPERVISOR", "SECRETARIAT"].includes(user.role);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [startOpen, setStartOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ employee_id: "", separation_type: "RESIGNATION", last_working_date: "", reason: "" });

  const load = useCallback(() => {
    api.get("/lifecycle", { params: { type } }).then((r) => setItems(r.data)).catch(() => {});
  }, [type]);
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [load]);

  const start = async () => {
    if (!form.employee_id) return toast.error("Pilih karyawan");
    try {
      if (type === "ONBOARDING") await api.post("/lifecycle/onboarding", { employee_id: form.employee_id });
      else await api.post("/lifecycle/offboarding", form);
      toast.success("Proses dimulai");
      setStartOpen(false);
      setForm({ employee_id: "", separation_type: "RESIGNATION", last_working_date: "", reason: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/lifecycle/${id}`);
    setDetail(data);
  };

  const toggleTask = async (taskId, done) => {
    const { data } = await api.put(`/lifecycle/${detail.id}/tasks/${taskId}`, { done });
    setDetail(data); load();
  };

  const complete = async () => {
    try { const { data } = await api.post(`/lifecycle/${detail.id}/complete`); setDetail(data); load(); toast.success("Proses diselesaikan"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const grouped = detail ? detail.tasks.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {}) : {};

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid={`start-${type.toLowerCase()}-button`} onClick={() => setStartOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> {type === "ONBOARDING" ? "Mulai Onboarding" : "Mulai Offboarding"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <p className="text-sm text-slate-400">Belum ada proses {type === "ONBOARDING" ? "onboarding" : "offboarding"}.</p>}
        {items.map((it) => (
          <Card key={it.id} data-testid="lifecycle-card" onClick={() => openDetail(it.id)}
            className="cursor-pointer rounded-2xl border-slate-200 p-5 transition-shadow hover:shadow-md dark:border-slate-800">
            <div className="flex items-center gap-3">
              <Avatar className="h-11 w-11"><AvatarImage src={it.avatar} /><AvatarFallback className="bg-primary/15 text-primary">{it.employee_name?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1">
                <p className="font-semibold">{it.employee_name}</p>
                <p className="text-xs text-slate-400">{it.position_name} · {it.department_name}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${it.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {it.status === "COMPLETED" ? "Selesai" : "Berjalan"}
              </span>
            </div>
            <div className="mt-4">
              <div className="mb-1 flex justify-between text-xs"><span className="text-slate-500">Progress</span><span className="font-mono font-semibold">{it.progress}%</span></div>
              <Progress value={it.progress} className="h-2" />
            </div>
          </Card>
        ))}
      </div>

      {/* Start dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{type === "ONBOARDING" ? "Mulai Onboarding" : "Mulai Offboarding"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Karyawan</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="lifecycle-employee-select"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.employee_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {type === "OFFBOARDING" && (
              <>
                <div className="space-y-2"><Label>Jenis Separasi</Label>
                  <Select value={form.separation_type} onValueChange={(v) => setForm({ ...form, separation_type: v })}>
                    <SelectTrigger data-testid="separation-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(SEP_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Hari Kerja Terakhir</Label>
                  <Input type="date" data-testid="last-working-date" value={form.last_working_date} onChange={(e) => setForm({ ...form, last_working_date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Alasan</Label>
                  <Textarea data-testid="offboard-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartOpen(false)}>Batal</Button>
            <Button data-testid="confirm-start-lifecycle" onClick={start}>Mulai</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.employee_name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4 dark:bg-slate-800/40">
                  <div>
                    <p className="text-sm text-slate-500">{detail.position_name} · {detail.department_name}</p>
                    {detail.type === "OFFBOARDING" && <p className="text-xs text-slate-400">{SEP_TYPES[detail.separation_type]} · {fmtDate(detail.last_working_date)}</p>}
                  </div>
                  <div className="text-right"><p className="font-mono text-2xl font-bold">{detail.progress}%</p></div>
                </div>

                <div className="space-y-3">
                  {Object.entries(grouped).map(([cat, tasks]) => (
                    <div key={cat}>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{cat}</p>
                      <div className="space-y-1">
                        {tasks.map((t) => (
                          <label key={t.id} data-testid="lifecycle-task" className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40">
                            <Checkbox checked={t.done} onCheckedChange={(v) => toggleTask(t.id, !!v)} disabled={detail.status === "COMPLETED"} data-testid="task-checkbox" />
                            <span className={t.done ? "text-slate-400 line-through" : ""}>{t.name}</span>
                            <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500 dark:bg-slate-800">{t.assigned_to}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-slate-100 p-3 dark:border-slate-800">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-500"><Clock className="h-3.5 w-3.5" /> Timeline</p>
                  <div className="space-y-1.5">
                    {detail.timeline.map((tl, i) => (
                      <p key={i} className="text-xs text-slate-500"><span className="font-mono text-slate-400">{fmtDate(tl.at)}</span> — {tl.text}</p>
                    ))}
                  </div>
                </div>

                {detail.status !== "COMPLETED" && canFinal && (
                  <Button className="w-full" data-testid="complete-lifecycle-button" onClick={complete} disabled={detail.progress < 100}>
                    {detail.progress < 100 ? `Selesaikan semua tugas dulu (${detail.progress}%)` : "Persetujuan Final & Selesaikan"}
                  </Button>
                )}
                {detail.status === "COMPLETED" && (
                  <p className="flex items-center justify-center gap-1 text-sm font-medium text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Proses selesai</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Lifecycle() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Siklus Karyawan</h1>
        <p className="mt-1 text-sm text-slate-500">Kelola proses onboarding karyawan baru dan offboarding karyawan keluar.</p>
      </div>
      <Tabs defaultValue="onboarding">
        <TabsList>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding"><UserPlus className="mr-1 h-4 w-4" /> Onboarding</TabsTrigger>
          <TabsTrigger value="offboarding" data-testid="tab-offboarding"><UserMinus className="mr-1 h-4 w-4" /> Offboarding</TabsTrigger>
        </TabsList>
        <TabsContent value="onboarding" className="mt-4"><LifecycleList type="ONBOARDING" /></TabsContent>
        <TabsContent value="offboarding" className="mt-4"><LifecycleList type="OFFBOARDING" /></TabsContent>
      </Tabs>
    </div>
  );
}
